/**
 * Das Handbuch.
 *
 * Bewusst *in* der Anwendung und nicht als externes Wiki: Fragen entstehen
 * beim Spielen, und der Weg zur Antwort soll kein Verlassen der Partie sein.
 * Nebenbei entfällt damit die Pflege einer zweiten Quelle, die genau dann
 * veraltet, wenn es niemand merkt – und die Erklärung eines Bereichs steht
 * einen Klick neben dem Bereich selbst.
 *
 * Der Inhalt liegt als Datensatz vor, nicht als eine lange Seite Markup. Das
 * ist keine Förmlichkeit: Nur so lassen sich Suche, Sprungliste und die
 * Verweise in die Spielbereiche aus derselben Quelle bauen, statt sie an drei
 * Stellen gleichlautend zu pflegen.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, EmptyState, Input, Mono, Notice, PageHeader } from '../components/ui';

interface HelpSection {
  id: string;
  title: string;
  /** Ein Satz darüber, worum es geht – steht unter der Überschrift. */
  summary: string;
  /**
   * Zielbereich in der Seitenleiste, relativ zur Partie.
   *
   * Ein Handbuch, das erklärt und dann alleinlässt, ist ein halbes Handbuch.
   */
  area?: { to: string; label: string };
  /**
   * Zusätzliche Suchbegriffe.
   *
   * Nötig, weil sich aus fertigem Markup kein verlässlicher Volltext ziehen
   * lässt. Hier stehen die Wörter, die jemand tippt, wenn er nicht die
   * Überschrift im Kopf hat – Abkürzungen, Fachbegriffe, Synonyme.
   */
  keywords: string;
  body: ReactNode;
}

interface HelpGroup {
  title: string;
  sections: HelpSection[];
}

/** Begriffserklärung im Fließtext. */
function T({ children }: { children: ReactNode }) {
  return <span className="font-medium text-panel-900 dark:text-panel-100">{children}</span>;
}

/** Aufzählung mit Begriff und Erklärung – das häufigste Muster hier. */
function Defs({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="space-y-2">
      {items.map(([begriff, erklaerung], i) => (
        <div key={i} className="grid gap-0.5 sm:grid-cols-[minmax(9rem,14rem)_1fr] sm:gap-3">
          <dt className="text-sm font-medium">{begriff}</dt>
          <dd className="text-sm text-panel-600 dark:text-panel-300">{erklaerung}</dd>
        </div>
      ))}
    </dl>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-panel-600 dark:text-panel-300">{children}</p>;
}

const GROUPS: HelpGroup[] = [
  {
    title: 'Einstieg',
    sections: [
      {
        id: 'grundlagen',
        title: 'Zeit, Tempo und Spielstände',
        summary: 'Wie die Simulation läuft und was beim Speichern passiert.',
        keywords: 'tick stunde geschwindigkeit pause vorspulen speichern autosave seed determinismus schwierigkeit',
        body: (
          <div className="space-y-3">
            <P>
              Ein <T>Tick</T> ist eine Spielstunde. Jeder Tick läuft in fester Reihenfolge ab: Zeit, Hardware,
              Energie und Wärme, BGP, Verkehr, Verträge, Markt, Support, Ereignisse, Forschung, Finanzen,
              Kennzahlen. Diese Reihenfolge ist der Grund, warum sich Ursache und Wirkung im Spiel immer
              gleich verhalten.
            </P>
            <P>
              Die Simulation rechnet <T>auf dem Server</T> weiter, auch wenn niemand zusieht – bis eine Partie
              nach einigen Minuten ohne Zuschauer gesichert und schlafen gelegt wird. Beim nächsten Öffnen
              läuft sie mit derselben Geschwindigkeit weiter; nur die Abwesenheit selbst wird nicht simuliert.
            </P>
            <Defs
              items={[
                ['Geschwindigkeit', 'Stufen von 1× bis 32×. Sie ändert nur das Tempo, nie das Ergebnis.'],
                ['Pause', 'Hält die Zeit an. Bauen, kaufen und konfigurieren geht weiter.'],
                [
                  'Vorspulen',
                  'Rechnet Stunden sofort durch, ohne zu warten – in Schritten von 6 h, 12 h oder einem ganzen Tag. Hält sich die Simulation dabei selbst an, etwa bei Zahlungsunfähigkeit, endet der Sprung dort.',
                ],
                ['Autosave', 'Läuft von selbst. Zusätzlich gibt es benannte Speicherplätze.'],
                [
                  'Determinismus',
                  'Derselbe Startwert ergibt denselben Spielverlauf. Ein exportierter Spielstand läuft anderswo exakt gleich weiter.',
                ],
              ]}
            />
            <Notice tone="info" title="Drei Schwierigkeitsgrade">
              Sie verändern Startkapital, Nachfrage und Kostendruck – nicht die Regeln. Wer die Mechanik
              einmal verstanden hat, versteht sie auf jeder Stufe.
            </Notice>
          </div>
        ),
      },
      {
        id: 'erste-schritte',
        title: 'Die ersten Schritte',
        summary: 'Was in einer neuen Partie zuerst dran ist.',
        area: { to: 'objectives', label: 'Ziele & Erfolge' },
        keywords: 'anfang start tutorial kampagne erste kunden was tun',
        body: (
          <div className="space-y-3">
            <P>
              Die <T>Kampagne</T> ist zugleich das Tutorial. Sie führt über 16 verkettete Meilensteine vom
              ersten Kunden bis zu zehn Gigabit Verkehr, und immer genau ein Ziel ist das nächste. Jedes Ziel
              trägt eine Begründung, warum dieser Schritt im echten Betrieb ein Meilenstein ist – wer der
              Reihe nach vorgeht, lernt das Spiel beim Spielen.
            </P>
            <P>
              Grob ist die Reihenfolge immer dieselbe: Ein Rechenzentrum eröffnen, ein Rack mieten, einen
              Server und einen Router hineinstellen, alles <T>verkabeln</T>, ein Prefix bekommen und
              ankündigen, Produkte mit Preisen anlegen. Erst dann kommen Kunden.
            </P>
            <Notice tone="warning" title="Der häufigste Anfängerfehler">
              Ein Server, der nicht an einem Switch-Port steckt, bedient keine Kunden. Das vergessene
              Patchkabel ist im Spiel eine echte Fehlerquelle – und fällt erst auf, wenn niemand bestellt.
            </Notice>
          </div>
        ),
      },
    ],
  },
  {
    title: 'Netzwerk',
    sections: [
      {
        id: 'bgp',
        title: 'BGP und Routing',
        summary: 'Wie das Netz seine Wege findet und warum die Auswahl so ausfällt.',
        area: { to: 'bgp', label: 'BGP' },
        keywords: 'bgp routing as-path local preference med route-map prefix-liste community session flap valley free leak fib dfz',
        body: (
          <div className="space-y-3">
            <P>
              BGP entscheidet, über welche Leitung der Verkehr das Netz verlässt. Der Entscheidungsprozess
              läuft in der üblichen Reihenfolge: <Mono>Local Preference</Mono> → lokal originiert →
              AS-Path-Länge → Origin → MED (nur bei gleichem Nachbar-AS) → eBGP vor iBGP → IGP-Metrik → Alter
              → Router-ID.
            </P>
            <P>
              Jede Pfadauswahl trägt eine <T>lesbare Begründung</T>. Wenn ein Ziel über den teuren Upstream
              statt über das kostenlose Peering läuft, steht dort, an welcher Stufe die Entscheidung gefallen
              ist. Das ist der schnellste Weg zur Ursache.
            </P>
            <Defs
              items={[
                ['Beziehungsarten', 'customer, peer, transit und ibgp – sie bestimmen, was exportiert werden darf.'],
                ['Route-Maps', 'match und set, dazu Prefix-Listen mit ge/le und AS-Path-Ausdrücke.'],
                ['Communities', 'inklusive NO_EXPORT, NO_ADVERTISE und Blackhole.'],
                ['Prepending', 'Verlängert den eigenen AS-Path künstlich, um eingehenden Verkehr zu lenken.'],
                ['Sitzungen', 'Voller Zustandsautomat von idle bis established, mit Flap-Zählung.'],
                ['IPv4 und IPv6', 'sind getrennte Adressfamilien und werden getrennt angekündigt.'],
              ]}
            />
            <Notice tone="danger" title="Valley-Free: der teuerste Konfigurationsfehler">
              Routen, die von einem Peer oder Upstream kommen, dürfen nicht an andere Peers weitergegeben
              werden. Wer das zulässt, erzeugt einen <T>Route Leak</T>: Der Upstream filtert, fremder Verkehr
              landet auf der eigenen Rechnung, und der Ruf sinkt. Eine ausgehende Route-Map ohne
              Valley-Free-Filter ist genau dieser Fall.
            </Notice>
            <P>
              Die weltweite Routingtabelle ist verdichtet auf einige Dutzend repräsentative Ziele, die je einen
              Anteil des Weltverkehrs tragen. Für <Mono>FIB</Mono>-Grenzen und Max-Prefix wird trotzdem die
              reale Größenordnung mitgeführt – ein zu kleiner Router verwirft Pakete, auch wenn die Anzeige
              nur wenige Einträge zeigt. <Mono>defaultOriginate</Mono> ist dafür die Lösung: Es kündigt
              wirklich nur eine Default-Route an.
            </P>
          </div>
        ),
      },
      {
        id: 'peering',
        title: 'Peering, IXP und Transit',
        summary: 'Warum ein Peering-Link allein noch nichts spart.',
        area: { to: 'ixp', label: 'Peering & IXP' },
        keywords: 'peering ixp transit upstream pni route-server port kosten 95 perzentil',
        body: (
          <div className="space-y-3">
            <P>
              <T>Transit</T> kostet Geld, <T>Peering</T> im Wesentlichen nur den Port. Abgerechnet wird Transit
              nach dem <T>95. Perzentil</T> – die höchsten fünf Prozent der Messwerte fallen heraus, kurze
              Spitzen sind also unschädlich, dauerhafte Last nicht.
            </P>
            <P>
              Peering-Anfragen laufen über einen IXP, einen Route-Server oder als privates Peering (PNI).
              Beitritt, Austritt und Portaufrüstung sind eigene Vorgänge.
            </P>
            <Notice tone="warning" title="Der Link allein senkt keine Kosten">
              Ein zusätzlicher Peering-Link spart erst dann, wenn die Routingpolitik den Verkehr auch dorthin
              lenkt. Der Verkehr folgt der Pfadauswahl, nicht der guten Absicht. Wer Peering aufbaut und den
              Transitanteil nicht sinken sieht, muss an der Local Preference ansetzen.
            </Notice>
          </div>
        ),
      },
      {
        id: 'ipam',
        title: 'IP-Adressen',
        summary: 'Blöcke, Subnetze, Verwendungszwecke und der IPv4-Markt.',
        area: { to: 'ipam', label: 'IP-Adressen' },
        keywords: 'ip adressen prefix subnetz split merge rir ipv4 handel transfer rdns reverse anycast',
        body: (
          <div className="space-y-3">
            <P>
              Adressen liegen in einer Hierarchie: <T>RIR-Zuteilung</T> → Subnetze → Einzeladressen. Blöcke
              lassen sich teilen und wieder zusammenführen, kaufen und verkaufen; IPv4-Transfers haben einen
              Marktwert, der sich mit der Weltlage bewegt.
            </P>
            <Defs
              items={[
                ['Verwendungszweck', 'unassigned, infrastructure, customer, loopback, transfer oder anycast.'],
                ['Ankündbarkeit', 'wird vor dem Ankündigen geprüft; ein Rückzug wirkt überall.'],
                ['Reverse-Zonen', 'rDNS je Prefix – fehlt es, meldet es der Assistent.'],
                ['Auslastung', 'wird laufend berechnet, inklusive Vergabe und Freigabe an Kunden.'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'loadbalancing',
        title: 'Lastausgleich: Pools, Health-Checks und Abzug',
        summary: 'Wie Verfügbarkeit aufhört, am einzelnen Server zu hängen – und was das kostet.',
        area: { to: 'loadbalancer', label: 'Loadbalancer' },
        keywords:
          'loadbalancer lastverteiler lastausgleich pool backend vip virtuelle adresse health check drain abzug session persistence sitzungsbindung tls termination round robin least connections ha vrrp redundanz',
        body: (
          <div className="space-y-3">
            <P>
              Ohne Lastausgleich liegt jeder Kunde auf <T>genau einem Server</T>. Fällt der aus, ist der Kunde eine
              volle Stunde offline – und das schlägt als SLA-Gutschrift und als Kündigungsgrund durch. Ein Pool aus
              zwei Knoten macht daraus die <T>Erkennungslücke des Health-Checks</T>: bei fünf Sekunden Intervall und
              drei Fehlversuchen fünfzehn Sekunden, und auch die nur für den Verkehrsanteil, der auf den toten Knoten
              fiel.
            </P>
            <P>
              Der Preis dafür ist ehrlich beziffert: <T>Jedes Mitglied trägt den vollen Ressourcenbedarf des Pools</T>.
              Eine Replik ist eine Replik – dieselbe Anwendung, dieselben Daten, nur woanders. Zwei Knoten Redundanz
              heißen zwei Knoten Hardware. Alles andere wäre Redundanz auf dem Papier.
            </P>
            <Defs
              items={[
                [
                  'Der Aufbau in vier Schritten',
                  'Gerät kaufen, verkabeln, Pool anlegen, Mitglieder aufnehmen – dann Verträge darauflegen. Jeder Schritt kann für sich schiefgehen, deshalb hat jeder seinen eigenen Ort.',
                ],
                [
                  'Ein Mitglied ist keine Redundanz',
                  'Ein Pool mit einem Knoten ist ein Umweg mit zusätzlichem Ausfallpunkt. Die Diagnose sagt es, weil man es sonst erst im Ausfall merkt.',
                ],
                [
                  'Der Balancer ist der neue Engpass',
                  'Hinter ihm sind die Server redundant, vor ihm ist es niemand. Erst ein Paar aus zwei gekoppelten Geräten schließt die Kette: Die Reserve übernimmt die virtuelle Adresse von selbst.',
                ],
                [
                  'Abzug (drain)',
                  'Der wichtigste Knopf: Ein Mitglied im Abzug bekommt keine neuen Sitzungen mehr, bedient die laufenden aber zu Ende. Danach lässt sich der Knoten warten, räumen und verkaufen, ohne dass ein Kunde etwas merkt. Genau dafür betreibt man Lastausgleich – nicht wegen der Last.',
                ],
                [
                  'Sitzungsbindung',
                  'Ohne sie landet derselbe Besucher bei jeder Anfrage auf einem anderen Knoten und ist abgemeldet. Bei Shared Hosting und Mail ist das keine Theorie, sondern die Beschwerde am nächsten Morgen. Kurios: Das Problem taucht erst auf, wenn man den zweiten Server dazustellt.',
                ],
                [
                  'TLS am Balancer',
                  'Nimmt den Backends die Handshakes ab und lädt sie sich selbst auf. Die Handshakes je Sekunde sind die unterschätzte Grenze: Ein Gerät bricht dort ein, lange bevor der Durchsatz erreicht ist.',
                ],
                [
                  'Verteilverfahren',
                  'Reihum verteilt stur und ignoriert, dass ein Knoten am Anschlag ist. Wenigste Verbindungen schaut hin und ist bei ungleicher Hardware fast immer besser. Nach Quelladresse erhält Sitzungen von selbst, verteilt dafür ungleich.',
                ],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'dienste',
        title: 'Dienste, VLANs und Verkabelung',
        summary: 'Was zwischen Gerät und Netz liegt.',
        area: { to: 'services', label: 'DNS & Dienste' },
        keywords: 'vlan dhcp vrf switch port trunk access dns zone ptr nat firewall acl ddos patchkabel verkabelung',
        body: (
          <div className="space-y-3">
            <Defs
              items={[
                ['VLANs', 'anlegen, Prefix zuweisen, DHCP-Pool aktivieren. Die VLAN-Konsistenz wird geprüft.'],
                ['Switch-Ports', 'als Access oder Trunk konfigurieren, erlaubte VLANs setzen, Ports schalten.'],
                ['VRFs', 'mit Route Distinguisher für getrennte Routingtabellen.'],
                ['DNS', 'Zonen und Einträge inklusive PTR, mit Erreichbarkeitsprüfung.'],
                ['NAT', 'inklusive Überbuchungsrechnung.'],
                ['Firewall', 'Regelwerke mit Standardregelsatz und Edge-ACL, dazu Mitigationskapazität gegen DDoS.'],
              ]}
            />
            <P>
              Verkabelt wird von Hand: Geräte an Switch-Ports stecken, abziehen, umpatchen; WAN-Strecken auf
              einen anderen Port oder Router umstecken. Dabei werden Geschwindigkeit, Standort und
              Portbelegung geprüft.
            </P>
          </div>
        ),
      },
    ],
  },
  {
    title: 'Rechenzentrum',
    sections: [
      {
        id: 'physik',
        title: 'Strom, Wärme und Ausfälle',
        summary: 'Die geschlossene Kette von der Last bis zum Defekt.',
        area: { to: 'datacenters', label: 'Rechenzentren' },
        keywords: 'strom energie wärme temperatur kühlung usv generator rack höheneinheiten pue standort ausfall',
        body: (
          <div className="space-y-3">
            <P>
              Die Kette ist geschlossen und läuft in eine Richtung: <T>Last → Stromverbrauch → Wärme →
              Temperatur → Gerätezustand → Ausfall</T>. Zu dichte Bestückung fällt deshalb nicht beim Einbau
              auf, sondern erst später durch Ausfälle.
            </P>
            <Defs
              items={[
                ['Standorte', 'Strompreis, Kundenpotenzial und Latenz hängen am Ort – 42 stehen zur Wahl.'],
                ['Ausbau', 'Stromversorgung, Kühlung und ein zweiter Netzanschluss sind getrennte Ausbaustufen.'],
                ['USV und Generator', 'überbrücken Netzausfälle, solange Kapazität und Treibstoff reichen.'],
                ['Racks', 'mieten und zurückgeben; Höheneinheiten, freie Slots und Rack-Leistung begrenzen.'],
                ['Gerätezustände', 'offline, booting, online, degraded, failed, maintenance.'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'standortvertrag',
        title: 'Standortverträge: verhandeln, kündigen, umziehen',
        summary: 'Ein Rechenzentrum gehört dir nicht – du mietest es, und darüber lässt sich reden.',
        area: { to: 'datacenters', label: 'Rechenzentren' },
        keywords:
          'colocation vertrag miete verhandeln aushandeln angebot gegenangebot laufzeit bindung kündigen kündigung frist ausstieg strafe umziehen umzug standortwechsel räumen transport spedition',
        body: (
          <div className="space-y-3">
            <P>
              Ein Standort ist ein <T>Mietverhältnis</T>: Du zahlst monatlich für Fläche, Strom und Kühlung, und der
              Vertrag dahinter hat Konditionen. Drei Stellschrauben bestimmen den Preis – <T>Laufzeit</T>, <T>zugesagte
              Leistung</T> in kW und <T>Vorauszahlung</T>. Wer sich lange bindet, viel zusagt und im Voraus zahlt,
              bekommt den Standort deutlich günstiger. Und kommt schlechter wieder heraus.
            </P>
            <P>
              Verhandeln ist ein Gespräch, kein Regler. Der Anbieter legt ein Angebot vor, du kannst bis zu dreimal
              nachfassen – aber jede Nachforderung kostet ihn Geduld. Maßvolle Gegenangebote bringen etwas, dreiste
              beenden das Gespräch. Unter seinen Selbstkostenpreis geht niemand. Wer es eilig hat, eröffnet sofort zum
              Listenpreis: zwölf Monate, kein Rabatt.
            </P>
            <Defs
              items={[
                [
                  'Kündigen, zwei Wege',
                  'Zum Ende der Mindestlaufzeit ist kostenlos, dauert aber. Sofort geht auch – dann wird die Restlaufzeit anteilig fällig. Beide Zahlen stehen im Dialog nebeneinander.',
                ],
                [
                  'Die Frist ist ein Termin',
                  'Was am Stichtag noch im Standort steht, räumt der Anbieter selbst: Geräte zum Schrottpreis, Räumungsgebühr obendrauf, und die Kunden auf diesen Servern sind weg. Eine Woche und einen Tag vorher wird gewarnt.',
                ],
                [
                  'Nach der Bindung steigt die Miete',
                  'Der Rabatt hing an der Laufzeit. Läuft sie aus, gilt wieder der Listenpreis – der Moment, neu zu verhandeln oder mit einem Monat Frist zu gehen.',
                ],
                [
                  'Hardware fährt Lastwagen',
                  'Dienste ziehen über die Leitung um, Geräte nicht: ausbauen, fahren, einbauen, hochfahren. Kosten und Fahrzeit hängen an Kilometern und Höheneinheiten, dazu ein Schadensrisiko, das mit Strecke und Alter steigt.',
                ],
                [
                  'Erst räumen, dann fahren',
                  'Ein Server im Transport liefert nichts aus – er muss vorher leer sein. Ein Router muss seine Strecken los sein, ein Switch seine Server: Ein Kabel zieht nicht mit um.',
                ],
                [
                  'Was sich nicht lohnt',
                  'Bei alter Hardware liegt der Transportpreis schnell über dem Zeitwert. Der Umzugsplan sagt es dazu – verkaufen und drüben neu kaufen ist dann günstiger und bringt neuere Geräte.',
                ],
              ]}
            />
            <Notice tone="info" title="Die übliche Reihenfolge eines Standortwechsels">
              Neuen Standort verhandeln → dort Racks mieten → Dienste per Live-Migration verlegen → leere Geräte fahren
              lassen → alten Standort kündigen. Wer in der umgekehrten Reihenfolge anfängt, arbeitet gegen eine Uhr.
            </Notice>
          </div>
        ),
      },
      {
        id: 'hardware',
        title: 'Hardware, Bauteile und Aufrüstung',
        summary: 'Ein Server ist ein Gehäuse mit Steckplätzen, kein festes Produkt.',
        area: { to: 'servers', label: 'Server' },
        keywords:
          'server router switch firewall usv kaufen verkaufen reparatur bauteile ram cpu sockel dimm ersatzteile lager wartungsfenster betriebssystem rolle konfigurator selbst bauen barebone gehäuse zusammenstellen eigenbau ausschalten abschalten reserve standby stilllegen strom sparen',
        body: (
          <div className="space-y-3">
            <P>
              Technische Daten werden aus der <T>Werksbestückung</T> abgeleitet und nicht von Hand gepflegt.
              Ein Gehäuse mit vier DIMM-Plätzen ist irgendwann voll, und getauschte kleine Module bleiben im
              Lager liegen.
            </P>
            <P>
              Der Ablauf beim Aufrüsten ist immer derselbe: <T>bestellen → Lieferzeit → Ersatzteillager →
              Wartungsfenster → Einbau</T>. Geprüft werden dabei Speicherstandard, CPU-Sockel und
              Laufwerks-Formfaktor. Vor der Ausführung gibt es eine Vorschau samt betroffener Verträge; liegen
              Kunden auf dem Server, verlangt das Wartungsfenster eine Bestätigung.
            </P>
            <Defs
              items={[
                ['Betriebssysteme', 'Debian, Ubuntu, Rocky, Windows Server, FreeBSD.'],
                ['Serverrollen', 'shared-hosting, vps-host, dedicated, storage, mail, dns, backup, unassigned.'],
                [
                  'Kerne oder Takt?',
                  'Der Katalog bietet beides getrennt an. Viele langsame Kerne tragen viele VPS gleichzeitig; wenige schnelle bedienen Shared Hosting und Datenbanken besser, weil dort die einzelne Anfrage zählt. Dazu gibt es sparsame Varianten: gleiche Kernzahl, ein Drittel weniger Strom, dafür niedriger Takt – das rechnet sich über die Stromrechnung, nicht über den Kaufpreis.',
                ],
                [
                  'Zwei Sockel',
                  'Dual-Socket-Gehäuse schaltet die Forschung „Dual-Socket-Verdichtung“ frei – danach stehen drei zur Wahl: ein bezahlbares Gen4-Gehäuse mit zwölf Einschüben, ein dichtes 1-HE-Gehäuse für knappen Rackplatz und die Gen5-Plattform für höchste Kerndichte.',
                ],
                ['Ersatzteile', 'bestellen (wahlweise Express), stornieren, Lagerbestand verkaufen. Teile altern.'],
                ['Verkauf', 'mit Restwertermittlung – wahlweise Bauteile ausschlachten statt komplett abgeben.'],
                [
                  'Reserve',
                  'Ein Server, den du gerade nicht brauchst, muss nicht weg: Abschalten legt ihn als Reserve still. Er zieht keinen Strom mehr, kostet aber weiter Wartung – und bleibt anders als ein ausgefallenes Gerät auch nach einem Stromausfall aus. Liegen noch Kunden darauf, fragt das Spiel nach.',
                ],
              ]}
            />
            <P>
              Statt aus dem Katalog zu wählen, lässt sich ein Server auch <T>selbst zusammenstellen</T>: leeres Gehäuse
              wählen, Sockel, DIMM-Plätze, Einschübe und Erweiterungsslots füllen. Die technischen Daten und der Preis
              entstehen aus der Bestückung, und es gelten dieselben Regeln wie beim Aufrüsten – Steckplätze sind
              endlich, Bauformen müssen passen, der Speichercontroller begrenzt. Ein selbst gebauter Server verhält sich
              danach wie jeder andere.
            </P>
            <P>
              Der Aufwand lohnt sich, wenn der Katalog danebenliegt: Ein Mailserver braucht Einschübe und kaum Kerne,
              ein VPS-Host das Gegenteil. Wer nur ein Fertiggerät nachbaut, zahlt einen kleinen Aufschlag für die
              Einzelbeschaffung – wer genau trifft, spart deutlich mehr.
            </P>
          </div>
        ),
      },
      {
        id: 'auslastung',
        title: 'Auslastung der Server lesen',
        summary: 'Warum „voll verkauft“ und „arbeitet viel“ zwei verschiedene Zahlen sind.',
        area: { to: 'servers', label: 'Server' },
        keywords: 'auslastung belegung last rechenlast kapazität voll überbuchung engste stelle engpass balken meter watt',
        body: (
          <div className="space-y-3">
            <P>
              Jeder Server zeigt zwei Balken, und sie meinen absichtlich Verschiedenes. Die <T>Belegung</T> sagt,
              wieviel von dem, was der Knoten anbieten kann, schon vergeben ist – sie entscheidet, ob der nächste
              Kunde noch daraufpasst. Die <T>Rechenlast</T> sagt, was er in diesem Moment tatsächlich tut; an ihr
              hängen Stromverbrauch und Wärme.
            </P>
            <P>
              Die beiden laufen regelmäßig auseinander: Ein bis zum Anschlag verkaufter Shared-Host kann im
              Leerlauf stehen, weil die Kunden gerade nichts abrufen, und ein halbleerer VPS-Host kann am Limit
              laufen. Wer nur eine der beiden Zahlen ansieht, kauft entweder zu früh oder zu spät.
            </P>
            <Defs
              items={[
                [
                  'Gemessen wird gegen die Verkaufsgrenze',
                  'Nicht gegen das Verbaute. Bei Shared Hosting liegt die Grenze um ein Vielfaches darüber – ein 64-GB-Server mit 200 GB Zusagen ist nicht überlastet, sondern planmäßig überbucht. Der Serverdialog nennt beide Zahlen.',
                ],
                [
                  'Engste Stelle',
                  'Über dem Balken steht die Ressource, die zuerst ausgeht – nur sie entscheidet über den nächsten Auftrag. Was nicht verbaut ist, zählt nicht mit: Ein Server ohne Festplatten ist nicht „zu 0 % ausgelastet“.',
                ],
                [
                  'Wo es steht',
                  'In der Serverliste je Zeile, im Serverdialog mit absoluten Zahlen, in der Rackbestückung – und unter Betrieb → Systeme als Rangliste mit dem vollsten Knoten oben.',
                ],
                [
                  'Nahe der Grenze',
                  'Die Kennzahl über der Serverliste zählt die Knoten ab 90 % Belegung. Der Gesamtdurchschnitt verdeckt genau die: Ein Bestand bei 40 % kann drei Server enthalten, die nichts mehr annehmen.',
                ],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'provisionierung',
        title: 'Provisionierung, Konsolidierung, Ausmusterung',
        summary: 'Überbuchung, Sammelumzüge und der Erneuerungszyklus.',
        area: { to: 'vps', label: 'VPS' },
        keywords: 'überbuchung shared vps migration konsolidierung consolidate retire ausmustern abstellgleis first fit',
        body: (
          <div className="space-y-3">
            <P>
              <T>Shared Hosting wird überbucht</T> – ein 512-GB-Server trägt mehrere hundert Pakete. <T>VPS
              nicht</T>: Dort ist RAM fest zugeteilt. Fehlende Kapazität kostet Interessenten, und der
              Assistent meldet das, bevor es teuer wird.
            </P>
            <P>
              Der wirtschaftliche Kern ist der <T>Erneuerungszyklus</T>: Ein neuer Knoten leistet so viel wie
              fünf alte, bei einem Bruchteil an Strom und Fläche. Wer die alten trotzdem laufen lässt, zahlt
              zweimal.
            </P>
            <Defs
              items={[
                ['Plan vor Vollzug', 'Der Konsolidierungsplan zeigt, was passt, was nicht und warum, wie lange es dauert und was frei wird. Er verändert nichts.'],
                ['Größte Last zuerst', 'Passt die große Instanz nicht mehr auf den mit kleinen zugestellten Knoten, bleibt sie liegen – und der Quellserver lässt sich nicht verkaufen.'],
                ['Abstellgleis', 'Ein vorgemerkter Server nimmt sofort keine Neukunden mehr an und verkauft sich selbst, sobald der letzte Dienst herunter ist. Jederzeit zurücknehmbar.'],
                ['Vorschläge', 'Das Spiel findet lohnende Zusammenlegungen selbst – nur solche, die einen Server vollständig räumen.'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'umzuege',
        title: 'Umzüge: Live-Migration und Vertragsumzug',
        summary: 'Warum ein Umzug eine Datenübertragung ist und nicht ein Knopfdruck.',
        area: { to: 'migrations', label: 'Umzüge' },
        keywords:
          'migration live migration umzug verlagern räumen evakuieren evacuate bandbreite uplink 10g dirty pages speicherabgleich umschaltpause cutover warteschlange drain',
        body: (
          <div className="space-y-3">
            <P>
              Ein Umzug ist <T>keine Wartezeit, sondern eine Übertragung</T>. Er dauert genau so lange, wie die
              Anbindung der beteiligten Knoten hergibt: über den Uplink des langsameren von beiden, und davon nur die
              Hälfte – die andere gehört den Kunden. Ein 1G-Port trägt damit rund <Mono>225 GB</Mono> pro Stunde. Wer
              regelmäßig große Instanzen bewegt, kauft in Wahrheit 10G-Ports.
            </P>
            <P>
              Der Ablauf hat drei Phasen: erst wandern die <T>Plattendaten</T> (das dauert und stört niemanden), dann
              wird der <T>Arbeitsspeicher nachgezogen</T>, zuletzt wird <T>umgeschaltet</T>. Nur der letzte Moment ist
              für den Kunden sichtbar.
            </P>
            <Defs
              items={[
                [
                  'Gleichzeitig heißt nicht schneller',
                  'Alle Übertragungen eines Servers teilen sich dieselbe Leitung. Zwanzig parallele Umzüge sind genauso schnell fertig wie zwanzig nacheinander – vier laufen, der Rest wartet sichtbar in der Schlange. Deshalb ist die Dauer eines Plans die Summe und nicht das Maximum.',
                ],
                [
                  'Die Instanz arbeitet dagegen',
                  'Während der Speicher übertragen wird, beschreibt die laufende Instanz ihn weiter – das muss noch einmal hinüber. Bei fetter Leitung fällt das nicht auf; bei dünner Leitung und hoher Last konvergiert es nie. Dann wird nach einigen Durchgängen erzwungen umgeschaltet, und aus dem Wimpernschlag wird eine Pause, die der Kunde bemerkt.',
                ],
                [
                  'Deshalb zieht man nachts um',
                  'Die Schreiblast folgt der Tageszeitkurve. Derselbe Umzug, der um 21 Uhr eine Pause kostet, läuft um 5 Uhr unbemerkt durch.',
                ],
                [
                  'Über Standorte hinweg',
                  'Möglich, aber nur über eigene oder gemietete Fasern mit freier Kapazität. Über gekauften Transit wird nicht migriert.',
                ],
                [
                  'Server räumen',
                  'Der häufigste Fall im Betrieb: „Diese Kiste muss raus.“ Anders als beim Zusammenlegen ist kein Ziel vorgegeben – jede Last sucht sich den passendsten freien Knoten. Auf Wunsch gibt sich der Server anschließend selbst ab.',
                ],
                [
                  'Mehrere auf einmal',
                  '„Dienste verlegen“ nimmt beliebig viele Dienste zum Anhaken und schickt sie auf dasselbe Ziel – gruppiert nach ihrem jetzigen Server, damit „alles von dieser Kiste weg“ ein Klick ist. Der Platz auf dem Ziel wird für die ganze Auswahl gerechnet, nicht je Dienst: Fünf Instanzen, die einzeln passen, passen zusammen womöglich nicht. Alle Dienste einer Auswahl brauchen dieselbe Serverrolle.',
                ],
                [
                  'Ganz ohne Unterbrechung',
                  'Ein Vertrag, der über einen Backend-Pool ausgeliefert wird, zieht unbemerkt um: Der Loadbalancer nimmt den Knoten aus der Rotation, die übrigen tragen weiter. Das ist der einzige Weg, die Umschaltpause vollständig loszuwerden.',
                ],
                [
                  'Und die Hardware selbst?',
                  'Geräte zwischen Standorten bewegt kein Netzwerk, sondern eine Spedition – siehe „Standortverträge“. Auf dieser Seite steht unter „Hardware unterwegs“, was gerade fährt.',
                ],
              ]}
            />
          </div>
        ),
      },
    ],
  },
  {
    title: 'Geschäft',
    sections: [
      {
        id: 'kunden',
        title: 'Kunden, Markt und Verträge',
        summary: 'Woher die Nachfrage kommt und warum sie wieder geht.',
        area: { to: 'customers', label: 'Kunden' },
        keywords: 'kunden nachfrage churn abwanderung ruf preis attraktivität segment vertrag kündigung sperrung reseller',
        body: (
          <div className="space-y-3">
            <P>
              Nachfrage entsteht aus drei Faktoren, und alle drei sind beeinflussbar: der <T>Preis</T> im
              Verhältnis zum Marktreferenzpreis, der <T>Ruf</T> des Unternehmens und das <T>Marktpotenzial</T>{' '}
              der belegten Standorte.
            </P>
            <Defs
              items={[
                ['Segmente', 'privat, KMU, Enterprise, Reseller – mit unterschiedlichen Ansprüchen.'],
                ['Vertragszustände', 'aktiv, gesperrt, gekündigt, ausstehend.'],
                ['Produkte', 'Preise setzen, aktiv oder inaktiv schalten. Die Attraktivität wird gegen den Markt gerechnet.'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'support',
        title: 'Support und Tickets',
        summary: 'Vier Größen entscheiden – und alle vier sind Entscheidungen, keine Zufälle.',
        area: { to: 'tickets', label: 'Tickets' },
        keywords: 'ticket support eskalation stufe level abuse frist deadline schicht fachgebiet zufriedenheit',
        body: (
          <div className="space-y-3">
            <Defs
              items={[
                ['Arbeitszeit', 'Ein Tagesteam ist nachts nicht da. Rund-um-die-Uhr-Support muss bezahlt werden.'],
                ['Fachgebiet', 'Ein Vertriebler kann eine Abuse-Beschwerde bearbeiten – langsam und mit geringerer Lösungsquote.'],
                ['Stufe', 'Was das 1st Level nicht schafft, geht ans 2nd, dann ans 3rd. Ohne höhere Stufen bleiben schwere Fälle liegen.'],
                ['Frist', 'Abuse-Beschwerden haben eine harte Deadline. Der Upstream wartet nicht.'],
              ]}
            />
            <P>
              Kategorien sind technical, billing, abuse, sales und outage; bei Ausfällen entstehen
              Störungstickets automatisch. Möglich sind Antworten, Rückfragen an Kunden (eine begrenzte Zahl
              ist kostenlos), Eskalation, Zuweisung und Sammelbearbeitung.
            </P>
            <Notice tone="danger" title="Ignorieren ist der teuerste Weg">
              Nach zwei Monaten ohne jede Antwort gibt der Kunde auf. Der Vorgang verschwindet, die
              Verärgerung nicht: Sie bleibt als Zähler am Kunden, benennt später den Kündigungsgrund und
              deckelt seine Erholung dauerhaft – je aufgegebenem Vorgang 20 Punkte, mindestens 40.
            </Notice>
          </div>
        ),
      },
      {
        id: 'personal',
        title: 'Personal',
        summary: 'Ohne Techniker bleibt defekte Hardware liegen.',
        area: { to: 'staff', label: 'Personal' },
        keywords: 'personal mitarbeiter techniker vertrieb sicherheit schicht einstellen entlassen gehalt',
        body: (
          <div className="space-y-3">
            <P>
              Rollen sind Techniker, Netzwerktechnik, Support, Vertrieb und Sicherheit. Einstellen, entlassen,
              Schichtmodell setzen und Fachgebiete zuweisen sind eigene Entscheidungen.
            </P>
            <Notice tone="warning" title="Die eine Stelle, an der Sparen sofort weh tut">
              Ohne Techniker bleibt defekte Hardware liegen – und ein Routerausfall wird endgültig.
            </Notice>
          </div>
        ),
      },
      {
        id: 'angebote',
        title: 'Verkaufsanrufe und Angebote',
        summary: 'Wer ein AS betreibt, bekommt ungefragt Angebote.',
        area: { to: 'offers', label: 'Angebote' },
        keywords: 'angebot verkaufsanruf carrier broker makler ipv4 gebraucht hardware bewertung listenpreis',
        body: (
          <div className="space-y-3">
            <P>
              Carrier mit Transit, Broker mit IPv4-Blöcken, Rechenzentren mit freier Fläche,
              Peering-Koordinatoren, Händler mit gebrauchter Hardware. Jedes Angebot ist zeitlich begrenzt.
            </P>
            <Notice tone="info" title="Der Vergleich steht dabei – man muss nur hinsehen">
              Jedes Angebot trägt seinen Vergleich zum Listenpreis mit sich. Dass eines 18 % über Markt liegt,
              ist sichtbar. Wer blind zusagt, zahlt drauf.
            </Notice>
          </div>
        ),
      },
      {
        id: 'wirtschaft',
        title: 'Wirtschaft und Finanzen',
        summary: 'Kosten kommen sofort, Geld kommt später. Das ist der eigentliche Druck.',
        area: { to: 'finance', label: 'Finanzen' },
        keywords: 'geld finanzen kosten erlös rechnung zahlungsziel steuer sla gutschrift kredit zins tilgung runway liquidität perzentil',
        body: (
          <div className="space-y-3">
            <Notice tone="warning" title="Die Verschiebung ist der Kern">
              Kosten fallen <T>stündlich</T> an, Erlöse kommen <T>monatlich</T> und mit Zahlungsziel. Wachstum
              kostet Hardware und Transit sofort – das Geld dafür kommt Wochen später.
            </Notice>
            <Defs
              items={[
                ['Transit', 'wird nach dem 95. Perzentil abgerechnet.'],
                ['Rechnungen', 'Netto, Steuer, Brutto; Zustände draft, open, paid, overdue, cancelled.'],
                ['SLA', 'Unterschreitungen erzeugen Gutschriften – gedeckelt.'],
                ['Betriebskosten', 'Strom, Kühlung und Personal, mit Effizienzfaktoren aus Forschung und Anlagen.'],
                ['Kredite', 'mit Zinsen; Schulden lassen sich gezielt tilgen.'],
                ['Runway', 'Die Liquiditätsreichweite sagt, wie lange das Geld noch reicht.'],
              ]}
            />
          </div>
        ),
      },
    ],
  },
  {
    title: 'Langfristig',
    sections: [
      {
        id: 'ereignisse',
        title: 'Ereignisse',
        summary: 'Zehn Typen, die meisten mit Entscheidung.',
        area: { to: 'diagnostics', label: 'Störungen' },
        keywords: 'ereignis stromausfall hardwaredefekt routerausfall glasfaser ddos sicherheitsvorfall kühlung wettbewerber konsole terminal tcpdump iptables syncookies',
        body: (
          <div className="space-y-3">
            <Defs
              items={[
                ['Stromausfall', 'abwarten oder Treibstoff nachkaufen'],
                ['Hardwaredefekt', 'reparieren oder ersetzen'],
                ['Routerausfall', 'reparieren oder ersetzen'],
                ['Glasfaserschaden', 'beschleunigen oder abwarten'],
                [
                  'DDoS-Angriff',
                  'blackholen, extern scrubben oder aushalten; die Art des Angriffs (UDP-Amplification, SYN-Flood, HTTP-Flood) zeigt die Konsole des Servers, dort helfen SYN-Cookies und iptables-Regeln',
                ],
                ['Sicherheitsvorfall', 'isolieren oder patchen'],
                ['Kühlungsausfall', 'Service rufen oder Last abwerfen'],
                ['Preissenkung des Wettbewerbs', 'mitgehen oder ignorieren'],
                ['Ohne Entscheidung', 'Nachfrageschub, positive Presse, Wartung beim Upstream, aggressives Konkurrenzangebot'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'forschung',
        title: 'Forschung',
        summary: '14 Projekte in vier Zweigen.',
        area: { to: 'research', label: 'Forschung' },
        keywords: 'forschung entwicklung 100g 400g scrubbing anycast nvme hypervisor kühlung energie automatik',
        body: (
          <div className="space-y-3">
            <Defs
              items={[
                ['Netz', '100G-Plattformen, 400G-Backbone, Traffic Engineering, Verteiltes Scrubbing, Anycast-DNS'],
                ['Rechenleistung', 'NVMe-Speicherplattform, Dual-Socket-Verdichtung, Optimierter Hypervisor'],
                ['Energie', 'Freie Kühlung, Dynamisches Energiemanagement, Abwärmenutzung'],
                ['Betrieb', 'Provisionierungsautomatik, Selbstheilende Systeme, Support-Werkzeuge'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'ziele',
        title: 'Ziele und Erfolge',
        summary: '16 Kampagnenziele als roter Faden, 14 Erfolge nebenher.',
        area: { to: 'objectives', label: 'Ziele & Erfolge' },
        keywords: 'ziele kampagne meilenstein erfolge achievement belohnung rufpunkte',
        body: (
          <div className="space-y-3">
            <P>
              Immer genau <T>ein</T> Ziel ist das nächste. Ziele mit offenen Vorgängern sind sichtbar, aber
              gesperrt, und ihr Begründungstext bleibt bis zur Freischaltung verdeckt – er ist die Belohnung
              fürs Ankommen.
            </P>
            <P>
              <T>Erfolge geben keinen Vorteil.</T> Sonst wäre das Abarbeiten einer Liste die beste Spielweise.
              Kampagnenziele geben Rufpunkte, kein Bargeld: Eine Prämie wäre in einem Wirtschaftssimulator
              eine Fremdwährung.
            </P>
            <P>
              Verhältnisziele tragen Untergrenzen gegen Scheinerfolge – ein Transitanteil von 0 % zählt bei
              0 Mbit/s Verkehr nicht.
            </P>
          </div>
        ),
      },
      {
        id: 'chronik',
        title: 'Chronik, Jahresbericht und Rekorde',
        summary: 'Warum die Vergangenheit erhalten bleibt, obwohl die Simulation aufräumt.',
        area: { to: 'chronicle', label: 'Chronik' },
        keywords: 'chronik historie geschichte monatsabschluss jahresbericht rekorde bestleistung langzeit kurve',
        body: (
          <div className="space-y-3">
            <P>
              Die Simulation löscht ihre Vergangenheit planmäßig: Das Monitoring reicht zehn Spieltage,
              Verträge werden nach drei und Rechnungen nach sechs Monaten entfernt. Damit trotzdem ein
              Gedächtnis bleibt, wird <T>vor dem Löschen verdichtet</T>: Zum Monatswechsel entsteht ein
              Abschluss aus rund vierzig Zahlen.
            </P>
            <Defs
              items={[
                ['Reichweite', '600 Monate, also 50 Spieljahre.'],
                ['Chronik', 'Bemerkenswerte Einzelereignisse, auf 200 Einträge begrenzt. Die Gründung fällt nie heraus.'],
                ['Jahresbericht', 'Kennzahlen mit Vorjahresvergleich, Kündigungen nach Ursache – dazu Deutungen aus einer Regelliste.'],
                ['Rekorde', 'Persönliche Bestleistungen: meiste Kunden, bester Monat, höchster Unternehmenswert und mehr.'],
              ]}
            />
          </div>
        ),
      },
      {
        id: 'warum',
        title: 'Ursachenzuordnung: „Warum?“',
        summary: 'Jede wichtige Kennzahl lässt sich aufklappen.',
        keywords: 'warum ursache erklärung aufklappen kennzahl herkunft attribution',
        body: (
          <div className="space-y-3">
            <P>
              Die Aufklärung erzeugt <T>keine neuen Daten</T> – sie liest, was der Tick ohnehin schreibt. Und
              sie wird erst auf Klick geladen: Wer nicht fragt, sieht nichts.
            </P>
            <Defs
              items={[
                ['Warum verliere ich Geld?', 'Hauptbuch nach Kategorie – exakt.'],
                ['Warum verliere ich Kunden?', 'Kündigungszähler nach Ursache (ausfall, support, preis, zahlung) – exakt.'],
                ['Warum sinkt mein Ruf?', 'Ausfallzeit, Ticketlage, Fristen, Hygiene – gewichtet.'],
                ['Warum steigen die Stromkosten?', 'Je Standort, mit PUE und Strompreis – Näherung.'],
                ['Warum steigen die Transitkosten?', 'Je Strecke, 95. Perzentil gegen Commit – exakt.'],
                ['Warum ist die Auslastung so hoch?', 'Knappste Ressource zuerst – exakt.'],
              ]}
            />
            <P>
              Beim Ruf ist die Zuordnung notwendigerweise eine Gewichtung: Er ist ein einziger träger
              Mischwert. Seine Bestandteile lassen sich benennen, aber nicht exakt aufrechnen – deshalb
              stehen dort Anteile und keine Punktbeträge.
            </P>
          </div>
        ),
      },
      {
        id: 'unternehmenswert',
        title: 'Unternehmenswert',
        summary: 'Die eine Zahl für „Wie gut stehe ich eigentlich da?“',
        keywords: 'unternehmenswert firmenwert bewertung anlagen kundenstamm schulden',
        body: (
          <div className="space-y-3">
            <P>
              Guthaben belohnt Nichtstun, die Kundenzahl ignoriert Schulden, der Monatsgewinn ignoriert
              Besitz. Der Unternehmenswert fasst zusammen:{' '}
              <T>Anlagen + IP-Vermögen + Kundenstamm + Kasse − Schulden</T>.
            </P>
            <P>
              Der Kundenstamm zählt als Jahresumsatz, <T>gewichtet mit Zufriedenheit und Ruf</T> – ein Kunde
              kurz vor der Kündigung ist kein voller Kunde.
            </P>
            <Notice tone="info" title="Keine Mechanik, nur ein Spiegel">
              Der Wert wird gelesen, nie verrechnet. Nichts in der Simulation hängt an ihm.
            </Notice>
          </div>
        ),
      },
      {
        id: 'welt',
        title: 'Die Welt außerhalb',
        summary: 'Fünf Systeme, die sich unabhängig vom Spieler entwickeln.',
        area: { to: 'world', label: 'Weltlage' },
        keywords: 'weltlage konjunktur regime rezession energiekrise chipknappheit index wettbewerber presse region trend',
        body: (
          <div className="space-y-3">
            <P>
              Kein Index, kein Wettbewerber und kein Regionaltrend kennt die eigene Kundenzahl, den eigenen
              Ruf oder die eigene Kasse. Berührt wird man nur über Marktanteile und Preisniveau.
            </P>
            <Defs
              items={[
                ['Konjunktur', 'Fünf Regime – ruhige Lage, Aufschwung, Rezession, Energiekrise, Chipknappheit. Aus einer Krise folgt nie unmittelbar dieselbe.'],
                ['Preisindizes', 'Sieben Stück um 1,0: Konjunktur, Strom, Hardware, IPv4, Transit, Glasfaser, Miete.'],
                ['Marktströmungen', 'Grunddrift je Produktart plus 10 Technikwellen. Höchstens zwei gleichzeitig, jede höchstens einmal.'],
                ['Regionen', 'Nachfrage, Strompreis und Miete je Standort, bewegt von 12 Regionaltrends. Sie wirken beim Verbrauch, nicht beim Bau.'],
                ['Wettbewerber', '14 Firmen als Modelle. Sie wachsen, senken Preise, fusionieren und gehen insolvent – und geben dabei Marktanteil frei.'],
                ['Fachpresse', 'Meldungen werden abgeleitet, nicht gewürfelt: Sie entstehen, wenn sich etwas messbar geändert hat.'],
              ]}
            />
            <Notice tone="success" title="Zugesichert und geprüft">
              Keine Sprünge (höchstens 1,2 % der Restdifferenz pro Spieltag), harte Grenzen (Hardware ≤ ×1,85,
              Strom ≤ ×2,3), keine ewige Krise, Rückkehr zur Mitte, Vorwarnzeit vor jeder Preisbewegung, der
              Markt ist nie über 85 % besetzt, und kein Produkt verschwindet je ganz.
            </Notice>
          </div>
        ),
      },
      {
        id: 'assistent',
        title: 'KI-Assistent und Fallstricke',
        summary: '19 Regeln, die laufend prüfen – und die Fehler, die sie finden.',
        area: { to: 'advisor', label: 'KI-Assistent' },
        keywords: 'assistent ki berater hinweis warnung diagnose fallstrick fehler',
        body: (
          <div className="space-y-3">
            <P>
              Der Assistent prüft den Zustand laufend und gibt begründete Hinweise – unter anderem zu einzelnem
              Upstream, geteiltem Backbone, fehlendem Generator, überlasteten Strecken, knapper Rechenleistung,
              erschöpftem IPv4, Rackfläche, BGP-Sitzungsgrenzen, FIB-Kapazität, Transit gegen Peering,
              ungenutzter Hardware, Liquiditätsreichweite, Ticketstau, heißen Geräten, alternder Hardware,
              fehlender Firewall, fehlendem rDNS und nicht angekündigten Prefixen.
            </P>
            <Defs
              items={[
                ['Mehr BGP-Nachbarn als der Router unterstützt', 'Keine Sitzung kommt hoch, das Netz bleibt ohne Routen.'],
                ['Volle Tabelle auf kleinem Router', 'Die FIB läuft über, Ziele werden zufällig unerreichbar.'],
                ['Nur ein Upstream', 'Ein Wartungsfenster trennt das gesamte Netz.'],
                ['Kein Techniker', 'Defekte Hardware bleibt liegen, ein Routerausfall wird endgültig.'],
                ['Route-Map ohne Valley-Free-Filter', 'Route Leak, der Upstream filtert, der Ruf sinkt.'],
                ['Racks zu dicht bestückt', 'Die Kühlung überlastet, Geräte drosseln und fallen aus.'],
              ]}
            />
          </div>
        ),
      },
    ],
  },
];

const ALLE = GROUPS.flatMap((g) => g.sections);

export function Help() {
  const [suche, setSuche] = useState('');

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return null;
    // Jedes Wort muss vorkommen – so verengt Tippen die Auswahl, statt sie zu
    // verbreitern, wie es eine Oder-Verknüpfung täte.
    const woerter = q.split(/\s+/);
    return ALLE.filter((s) => {
      const heuhaufen = `${s.title} ${s.summary} ${s.keywords}`.toLowerCase();
      return woerter.every((w) => heuhaufen.includes(w));
    });
  }, [suche]);

  const sichtbar = treffer ?? ALLE;

  return (
    <div>
      <PageHeader
        title="Handbuch"
        description="Jeder Bereich des Spiels, einmal erklärt – von der Zeitrechnung bis zur Weltwirtschaft."
      />

      <div className="grid gap-4 lg:grid-cols-[15rem_1fr] lg:items-start">
        {/* Sprungliste: auf breiten Schirmen mitlaufend, damit man beim Lesen
            die Gliederung nicht verliert. */}
        <aside className="lg:sticky lg:top-4">
          <div className="surface p-3">
            <Input
              label="Suchen"
              placeholder="z. B. Peering, Ticket, Strom"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
            <nav className="mt-3 space-y-3">
              {GROUPS.map((group) => {
                const offen = group.sections.filter((s) => sichtbar.includes(s));
                if (offen.length === 0) return null;
                return (
                  <div key={group.title}>
                    <p className="label px-1 pb-1">{group.title}</p>
                    <ul className="space-y-0.5">
                      {offen.map((s) => (
                        <li key={s.id}>
                          <a
                            href={`#${s.id}`}
                            className="block truncate rounded px-2 py-1 text-xs text-panel-600 hover:bg-panel-100 hover:text-panel-900 dark:text-panel-300 dark:hover:bg-panel-800 dark:hover:text-panel-50"
                          >
                            {s.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="min-w-0 space-y-4">
          {treffer && (
            <p className="text-xs text-panel-500 dark:text-panel-400">
              {treffer.length === 0
                ? 'Kein Abschnitt passt.'
                : `${treffer.length} von ${ALLE.length} Abschnitten passen.`}
            </p>
          )}

          {sichtbar.length === 0 && (
            <EmptyState
              title="Nichts gefunden"
              description="Andere Schreibweise versuchen, oder die Suche leeren, um wieder alles zu sehen."
            />
          )}

          {GROUPS.map((group) => {
            const offen = group.sections.filter((s) => sichtbar.includes(s));
            if (offen.length === 0) return null;
            return (
              <section key={group.title} className="space-y-4">
                <h2 className="label pt-2">{group.title}</h2>
                {offen.map((section) => (
                  // Der Anker sitzt auf einem eigenen Element mit Abstand nach
                  // oben, sonst verschwindet die Überschrift beim Springen
                  // unter dem Seitenkopf.
                  <div key={section.id} id={section.id} className="scroll-mt-4">
                    <Card
                      title={section.title}
                      subtitle={section.summary}
                      actions={
                        section.area && (
                          <Link to={`../${section.area.to}`} relative="path">
                            <Badge tone="info">{section.area.label} öffnen →</Badge>
                          </Link>
                        )
                      }
                    >
                      {section.body}
                    </Card>
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
